import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Gavel, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

const statusCell = (g) => {
  if (g.status === "set") {
    return <span className="text-emerald-700 font-semibold">Θα οριστεί ({g.case_count - g.already_set - g.kept_manual} υποθ.)
      {g.kept_manual > 0 && <span className="text-slate-500 font-normal"> · {g.kept_manual} χειροκίνητοι διατηρούνται</span>}</span>;
  }
  if (g.status === "unchanged") return <span className="text-slate-500">Ήδη ορισμένος</span>;
  if (g.status === "skipped_multiple") {
    return <span className="text-orange-700">Παράλειψη — πολλοί δικαστές: {g.pretrial_judges.join(", ")}</span>;
  }
  return <span className="text-slate-400">—</span>;
};

// Admin → Εργαλεία Συστήματος: Δικαστές Έδρας από Προδικαστικό
export const BenchJudgeSyncCard = () => {
  const [autoSync, setAutoSync] = useState(false);
  const [savingToggle, setSavingToggle] = useState(false);
  const [overwrite, setOverwrite] = useState(false);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [plan, setPlan] = useState(null);
  const [result, setResult] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState("");
  const [courtType, setCourtType] = useState("");

  useEffect(() => {
    axios.get(`${API}/admin/bench-judge-sync/settings`)
      .then(r => setAutoSync(!!r.data?.auto_sync)).catch(() => {});
    axios.get(`${API}/admin/bench-judge-sync/branches`)
      .then(r => setBranches(r.data?.branches || [])).catch(() => {});
  }, []);

  const qs = () => `overwrite=${overwrite}&court_type=${encodeURIComponent(courtType)}&branch=${encodeURIComponent(branch)}`;
  const filterLabel = [branch || "Όλα τα καταστήματα", courtType || "Όλοι οι τύποι δικαστηρίου"].join(" · ");

  const toggleAuto = async (val) => {
    try {
      setSavingToggle(true);
      const r = await axios.put(`${API}/admin/bench-judge-sync/settings`, { auto_sync: val });
      setAutoSync(!!r.data?.auto_sync);
      toast.success(val ? "Αυτόματος συγχρονισμός ΕΝΕΡΓΟΣ" : "Αυτόματος συγχρονισμός ανενεργός");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα αποθήκευσης ρύθμισης");
    } finally { setSavingToggle(false); }
  };

  const preview = async () => {
    try {
      setLoading(true); setResult(null);
      const r = await axios.get(`${API}/admin/bench-judge-sync/preview?${qs()}`, { timeout: 120000 });
      setPlan(r.data);
      toast.info(`Προεπισκόπηση: ${r.data.to_set} πινάκια/δικάσιμοι θα ενημερωθούν (${r.data.cases_to_update} υποθέσεις)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα προεπισκόπησης");
    } finally { setLoading(false); }
  };

  const execute = async () => {
    if (!window.confirm(`Επιβεβαίωση: Ορισμός Δικαστών Έδρας από τους δικαστές προδικαστικού;\n\nΦίλτρο: ${filterLabel}`)) return;
    try {
      setExecuting(true);
      const r = await axios.post(`${API}/admin/bench-judge-sync/execute?${qs()}`, null, { timeout: 180000 });
      setResult(r.data); setPlan(r.data);
      toast.success(r.data.message || "Ολοκληρώθηκε");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα εκτέλεσης");
    } finally { setExecuting(false); }
  };

  return (
    <div className="p-4 rounded-lg border bg-yellow-50 border-yellow-300" data-testid="bench-sync-card">
      <h3 className="font-medium text-yellow-900 flex items-center gap-2"><Gavel className="w-4 h-4" />Δικαστές Έδρας από Προδικαστικό Έλεγχο</h3>
      <p className="text-sm text-yellow-800 mt-1">
        Ο δικαστής που έκανε τον <strong>προδικαστικό έλεγχο</strong> ορίζεται αυτόματα και ως <strong>Δικαστής Έδρας</strong> στο
        αντίστοιχο πινάκιο και δικάσιμο των υποθέσεών του. Ορίζεται ΜΟΝΟ όταν στη δικάσιμο υπάρχει ένας μοναδικός
        δικαστής προδικαστικού — αλλιώς παραλείπεται και αναφέρεται. Χειροκίνητοι δικαστές έδρας διατηρούνται.
      </p>
      <div className="flex flex-wrap items-end gap-3 mt-3">
        <div className="space-y-1">
          <Label className="text-xs text-yellow-900">Κατάστημα</Label>
          <Select value={branch || "__all__"} onValueChange={(v) => setBranch(v === "__all__" ? "" : v)}>
            <SelectTrigger className="h-8 w-64 text-xs bg-white" data-testid="bench-sync-branch-select">
              <SelectValue placeholder="Όλα τα καταστήματα" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Όλα τα καταστήματα</SelectItem>
              {branches.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-yellow-900">Τύπος Δικαστηρίου</Label>
          <Select value={courtType || "__all__"} onValueChange={(v) => setCourtType(v === "__all__" ? "" : v)}>
            <SelectTrigger className="h-8 w-48 text-xs bg-white" data-testid="bench-sync-court-type-select">
              <SelectValue placeholder="Όλοι" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Όλοι οι τύποι</SelectItem>
              <SelectItem value="Μονομελές">Μονομελές</SelectItem>
              <SelectItem value="Πολυμελές">Πολυμελές</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-5 mt-3">
        <label className="flex items-center gap-2 cursor-pointer" data-testid="bench-sync-auto-toggle-label">
          <Switch checked={autoSync} onCheckedChange={toggleAuto} disabled={savingToggle} data-testid="bench-sync-auto-toggle" />
          <span className="text-sm font-medium text-yellow-900">Αυτόματος συγχρονισμός σε κάθε ορισμό δικαστή προδικαστικού</span>
          {autoSync && <Badge className="bg-emerald-600 text-white border-0 text-[10px]">ΕΝΕΡΓΟΣ</Badge>}
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox checked={overwrite} onCheckedChange={(v) => setOverwrite(!!v)} data-testid="bench-sync-overwrite" />
          <Label className="text-sm text-yellow-900 cursor-pointer">Αντικατάσταση και χειροκίνητων δικαστών έδρας</Label>
        </label>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        <Button variant="outline" className="border-blue-400 text-blue-700 hover:bg-blue-50" onClick={preview} disabled={loading}
                data-testid="bench-sync-preview-btn">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Προεπισκόπηση...</> : "Προεπισκόπηση"}
        </Button>
        <Button className="bg-yellow-600 hover:bg-yellow-700 text-white" onClick={execute} disabled={executing}
                data-testid="bench-sync-execute-btn">
          {executing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Εκτέλεση...</> : <><Gavel className="w-4 h-4 mr-2" />Εκτέλεση Ορισμού</>}
        </Button>
      </div>
      {result && <p className="text-sm text-emerald-700 mt-2 font-medium" data-testid="bench-sync-result-msg">{result.message}</p>}
      {plan && (
        <div className="mt-3 p-3 bg-white border border-yellow-300 rounded text-xs space-y-2" data-testid="bench-sync-result">
          <p className="text-[11px] text-slate-500" data-testid="bench-sync-filter-label">Φίλτρο: <b>{filterLabel}</b></p>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="border-blue-300 text-blue-700">Πινάκια/Δικάσιμοι: {plan.groups_total}</Badge>
            <Badge variant="outline" className="border-emerald-300 text-emerald-700">{result ? "Ενημερώθηκαν" : "Θα ενημερωθούν"}: {result ? result.groups_updated : plan.to_set} ({result ? result.cases_updated : plan.cases_to_update} υποθ.)</Badge>
            <Badge variant="outline" className="border-slate-300 text-slate-600">Ήδη ορισμένοι: {plan.unchanged}</Badge>
            <Badge variant="outline" className="border-orange-300 text-orange-700">Πολλοί δικαστές: {plan.skipped_multiple}</Badge>
            <Badge variant="outline" className="border-slate-300 text-slate-500">Χωρίς δικαστή προδ.: {plan.skipped_none}</Badge>
          </div>
          {plan.groups?.length > 0 && (
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="w-full text-[11px] border-collapse">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="p-1.5 text-left border">Πινάκιο</th>
                    <th className="p-1.5 text-left border">Δικάσιμος</th>
                    <th className="p-1.5 text-center border">Υποθ.</th>
                    <th className="p-1.5 text-left border">Δικαστής Έδρας</th>
                    <th className="p-1.5 text-left border">Κατάσταση</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.groups.map((g, i) => (
                    <tr key={i} className={g.status === "set" ? "bg-emerald-50" : g.status === "skipped_multiple" ? "bg-orange-50" : ""}
                        data-testid={`bench-sync-row-${i}`}>
                      <td className="p-1.5 border font-mono">{g.pinakio_label}</td>
                      <td className="p-1.5 border">{fmtDate(g.hearing_date)}</td>
                      <td className="p-1.5 border text-center">{g.case_count}</td>
                      <td className="p-1.5 border font-semibold">{g.judge || "—"}</td>
                      <td className="p-1.5 border">{statusCell(g)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {plan.groups_truncated > 0 && <p className="text-[10px] text-slate-500 italic mt-1">… και {plan.groups_truncated} ακόμη πινάκια/δικάσιμοι</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BenchJudgeSyncCard;
